import React, { useState } from 'react';
import { Search, Loader2, X, Target, Zap, ArrowRight, ExternalLink, Globe, Compass, Shield } from 'lucide-react';

const OfferScoutModal = ({ isOpen, onClose, onSelectCompetitor }) => {
  const [query, setQuery] = useState('');
  const [isScouting, setIsScouting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleScout = async () => {
    if (!query) return;
    setIsScouting(true);
    setError(null);
    setResults(null);

    try {
      const res = await fetch('/api/scout/search-competitors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResults(data);
      }
    } catch (err) {
      setError('Failed to reach the Scout server.');
    } finally {
      setIsScouting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700/50 rounded-[3rem] w-full max-w-2xl shadow-[0_0_100px_rgba(59,130,246,0.15)] overflow-hidden relative flex flex-col transform animate-in zoom-in-95 duration-300 max-h-[90vh]">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>
        
        <div className="p-10 flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between mb-8 shrink-0">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-600/20 text-blue-400 rounded-2xl border border-blue-500/20 shadow-inner">
                <Compass size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white tracking-tight uppercase">Offer Scout</h3>
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mt-0.5">Find Live Competitor Funnels</p>
              </div>
            </div>
            <button onClick={onClose} className="p-3 text-slate-500 hover:text-white hover:bg-slate-800 rounded-2xl transition-all">
              <X size={24} />
            </button>
          </div>

          {!results ? (
            <div className="space-y-8 animate-in fade-in py-4">
              <div className="space-y-4">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Product Name or Offer Topic</label>
                <div className="flex space-x-3">
                  <input 
                    type="text" 
                    value={query} 
                    onChange={e => setQuery(e.target.value)} 
                    placeholder="e.g. Alpilean, Dog Training, Solar Panels..." 
                    className="flex-1 bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all font-mono text-sm shadow-inner placeholder:text-slate-700" 
                    onKeyDown={(e) => e.key === 'Enter' && handleScout()}
                  />
                  <button 
                    onClick={handleScout} 
                    disabled={!query || isScouting} 
                    className="px-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-2xl shadow-lg shadow-blue-500/20 transition-all flex items-center group active:scale-95"
                  >
                    {isScouting ? <Loader2 className="animate-spin size-5" /> : <Search size={20} className="group-hover:scale-110 transition-transform" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 ml-1 italic leading-relaxed">The Ninja Scout uses AI to generate advanced search footprints that reveal hidden affiliate landing pages and advertorials.</p>
              </div>

              {error && (
                <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-start space-x-3 text-rose-400">
                  <Target size={18} className="shrink-0 mt-0.5" />
                  <p className="text-xs font-bold leading-relaxed">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-950/30 border border-slate-800 rounded-2xl text-center space-y-2">
                  <Globe size={20} className="text-blue-400 mx-auto" />
                  <div className="text-[8px] font-black text-white uppercase">Global Recon</div>
                </div>
                <div className="p-4 bg-slate-950/30 border border-slate-800 rounded-2xl text-center space-y-2">
                  <Shield size={20} className="text-indigo-400 mx-auto" />
                  <div className="text-[8px] font-black text-white uppercase">Bypass Cloakers</div>
                </div>
                <div className="p-4 bg-slate-950/30 border border-slate-800 rounded-2xl text-center space-y-2">
                  <Target size={20} className="text-emerald-400 mx-auto" />
                  <div className="text-[8px] font-black text-white uppercase">Direct Hit</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-8 animate-in slide-in-from-bottom-4">
              <div className="p-6 bg-blue-500/5 border border-blue-500/20 rounded-3xl">
                <div className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-2 flex items-center">
                  <Zap size={14} className="mr-2" /> Scout Strategy
                </div>
                <p className="text-xs text-blue-100/80 leading-relaxed italic">
                  {results.strategy}
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-4 flex items-center">
                    <Target size={14} className="mr-2" /> High-Value Search Footprints
                  </div>
                  <div className="space-y-3">
                    {results.footprints.map((fp, i) => (
                      <div key={i} className="group flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-2xl hover:border-blue-500/50 transition-all">
                        <code className="text-xs text-blue-400 font-mono">{fp}</code>
                        <a 
                          href={`https://www.google.com/search?q=${encodeURIComponent(fp)}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 bg-slate-900 text-slate-400 hover:text-white rounded-xl transition-colors"
                        >
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-4 flex items-center">
                    <Globe size={14} className="mr-2" /> Likely Competitor Domains
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {results.likelyPatterns.map((pattern, i) => (
                      <div key={i} className="flex items-center space-x-3 p-4 bg-slate-900/50 border border-slate-800 rounded-2xl">
                        <div className="size-2 bg-indigo-500 rounded-full"></div>
                        <span className="text-xs text-white font-bold">{pattern}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button 
                  onClick={() => setResults(null)} 
                  className="w-full py-5 bg-slate-800 hover:bg-slate-700 text-white font-black rounded-2xl transition-all uppercase tracking-widest text-xs active:scale-95"
                >
                  Scout Another Product
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferScoutModal;
